(async () => {
    const { Core } = await importModule('Core');
    const { fetchHTML, $replaceContent } = await importModule('Utils');

    // Keep in step with url_only_filters in snippets/collection-filter.liquid.
    const URL_ONLY_FILTERS = ['filter.p.m.rbc.category'];

    customElements.define('collection-provider', class extends Core {

        elements = {
            form: '[data-collection-form]',
            drawerForm: '[data-collection-drawer-form]',
            dynamicBlocks: '*[data-collection-dynamic-block]'
        }

        subscriptions = {
            'filter:change': '_onFilterChange',
            'navigation:change': '_onFilterChange'
        }

        cache = {};

        render() {
            this.subscribe('filter:change');
            this.subscribe('navigation:change');
            // Back / Forward change the URL without any of our own events: re-render the grid from it, or the grid keeps
            // showing the filters, sort or page the reader has just left.
            this._rendered = new URLSearchParams(window.location.search).toString();
            this._onPopState = () => {
                if (new URLSearchParams(window.location.search).toString() === this._rendered) return; // a hash-only move
                this._onFilterChange({
                    url: `${window.location.pathname}${window.location.search}`,
                    replace: true,   // the browser has already moved; do not add an entry
                    noScroll: true,  // the browser restores the scroll position itself
                    keepHash: true,
                    fromHistory: true // listeners must not rewrite the entry the reader has just returned to
                });
            };
            window.addEventListener('popstate', this._onPopState);
        }

        disconnect() {
            window.removeEventListener('popstate', this._onPopState);
        }

        async _onFilterChange(data) {
            this._getUrlParams(data);
            this._rendered = this.urlParams.toString();
            this._updateHistory(data);
            // Opt-ins (hub pages): a caller narrowing the grid from elsewhere on the page passes noScroll so the reader stays put.
            if (!data?.noScroll) this._scrollToTop();
            await this._updateDynamicBlocks(data);
        }

        _getUrlParams(data) {
            if (data?.url) {
                this.urlParams = new URL(`${window.location.origin}${data.url}`).searchParams;
                return;
            }
            const form = this.$currentForm;
            this.urlParams = new URLSearchParams(new FormData(form));
            // A URL rebuilt from the form drops whatever the form cannot know about; carry that across from the current URL.
            // (1) URL-only filters never have an input (snippets/collection-filter.liquid hides the group).
            // (2) The drawer's inputs sit in a <template> until the drawer is first opened, so on a phone a page that was
            //     opened already filtered (a link, a refresh) has no filter inputs at all: changing the sort lost the filters.
            const current = new URLSearchParams(window.location.search);
            const formHasFilters = form ? Array.from(form.elements).some(el => el.name?.startsWith('filter.')) : false;
            const carry = formHasFilters
                ? URL_ONLY_FILTERS
                : Array.from(new Set(Array.from(current.keys()).filter(name => name.startsWith('filter.'))));
            carry.forEach(name => {
                if (!this.urlParams.has(name)) current.getAll(name).forEach(value => this.urlParams.append(name, value));
            });
        }

        _updateHistory(data) {
            // Default is unchanged (pushState, no hash). replace: no new history entry; keepHash: carry the current #fragment over.
            const query = this.urlParams.toString();
            const url = `${window.location.pathname}${query ? `?${query}` : ''}${data?.keepHash ? window.location.hash : ''}`;
            window.history[data?.replace ? 'replaceState' : 'pushState']({}, null, url);
        }

        async _updateDynamicBlocks(data) {
            // Only the latest request may publish and swap. With two quick changes the older, slower response used to
            // arrive last and overwrite the grid while the URL already showed the newer selection.
            const request = this._request = (this._request || 0) + 1;
            const doc = await this._getDoc();
            if (request !== this._request) return;
            this.doc = doc;
            this._syncSort(doc);
            this.publish('collection:updated', { doc });
            const swap = () => this.$dynamicBlocks.map($block => {
                $replaceContent(
                    $block,
                    doc.getElementById($block.id)
                );
            });
            // Opt-in hook (hub pages): a listener may preventDefault() and run detail.swap() itself, e.g. inside a view transition.
            if (this.dispatchEvent(new CustomEvent('collection:swap', { bubbles: true, cancelable: true, detail: { swap, fromHistory: !!data?.fromHistory } }))) swap();
        }

        // The sort control sits outside the dynamic blocks. After Back / Forward it kept showing the sort the reader had
        // just left, and the next form-based change resubmitted that stale sort. The fetched document knows the right
        // value (including the default when the URL names none): copy it, and let the drop-down repaint its label and
        // selected item — without a change event, which would start another request.
        _syncSort(doc) {
            const source = doc.querySelector('select[name="sort_by"]');
            if (!source) return;
            this.querySelectorAll('select[name="sort_by"]').forEach(select => {
                if (select.value === source.value) return;
                select.value = source.value;
                select.closest('drop-down')?._onSelectChange?.({ target: select });
            });
        }

        async _getDoc() {
            const urlParamsStr = this.urlParams.toString();
            if(!this.cache[urlParamsStr]) {
                this.publish('collection:loading');
                this.cache[urlParamsStr] = await this._fetchDoc(urlParamsStr);
            }
            return this.cache[urlParamsStr];
        }

        _fetchDoc(urlParamsStr) {
            const fetchParams = new URLSearchParams(urlParamsStr);
            fetchParams.append('section_id', this.sectionId);
            return fetchHTML(`${this.getAttribute('collection-url')}?${fetchParams.toString()}`);
        }

        _scrollToTop() {
            window.scrollTo({
                top: this.topPoint,
                behavior: 'smooth',
            });
        }

        get topPoint() {
            const headerHeight = document.documentElement.style.getPropertyValue('--header-height')?.replace('px', '');
            return headerHeight ? this.offsetTop - headerHeight : this.offsetTop;
        }

        get $currentForm() {
            if(!this.$form) {
                return this.$drawerForm;
            }
            return this.onMobile ? this.$drawerForm : this.$form;
        }

        get onMobile() {
            return window.matchMedia('(max-width: 992px)').matches;
        }
    })
})();